/**
 * INSTR-016 — Hooks des liens d'action signés.
 *
 * Côté OF (authentifié — apiFetch) : émission (pièce / auto-déclaration),
 * file « Dépôts à valider », validation / rejet / révocation.
 * Côté PUBLIC (page /action/:token) : `fetch` NU sans credentials
 * (calque useSignCeremony — jamais apiFetch, pas de redirect 401).
 *
 * ## Où lire quoi
 *
 * | Sujet | Fichier |
 * |---|---|
 * | Types du contrat, endpoint, en-têtes | `useInstructorActionLinks.shared.ts` |
 * | Actions en masse + aperçu d'impact | `useInstructorActionLinks.bulk.ts` |
 * | Page publique (`fetch` nu, sans compte) | `useInstructorActionLinks.public.ts` |
 * | Émission, file de validation, décisions | ce fichier |
 *
 * ⚠️ **Ce fichier reste le POINT D'ENTRÉE.** 12 composants et 10 fichiers de
 * test importent d'ici, dont 8 qui posent `vi.mock('@/hooks/useInstructorActionLinks')` :
 * un appelant rebranché sur un sous-module échapperait à leur mock. La surface
 * publique doit rester identique au découpage — d'où les ré-exports ci-dessous.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ACTION_LINKS_ENDPOINT, invalidateActionLinks } from '@/lib/action-links-cache'
import { apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import { codeDuRefusTransverse } from '@/lib/api-error-transverse'
import { invalidateDataTableListings } from '@/lib/datatable-invalidate'
import { queryKeys } from '@/lib/queryKeys'
import { invalidateRequirementsMatrix } from '@/lib/requirements-matrix-cache'

import { ENDPOINT, headers } from './useInstructorActionLinks.shared'
import type {
    ActionLinkQueueItem,
    ActionLinksSummary,
    RecipientCheck,
} from './useInstructorActionLinks.shared'

// L'endpoint et son invalidation vivent dans `lib/action-links-cache` : le
// cockpit « Suivi des exigences » émet lui aussi des demandes et doit invalider
// les mêmes clés, sans que les deux modules de hooks s'importent l'un l'autre.
// Ré-exportés pour les appelants historiques (`InstructorRequestsPage`).
export { ACTION_LINKS_ENDPOINT }

// ── Surface publique ré-exportée (identique à l'avant-découpage) ─────────────

export type {
    ActionLinkQueueItem,
    ActionLinkStatus,
    ActionLinksSummary,
    PublicActionLinkView,
    PublicPendingItem,
    RecipientCheck,
} from './useInstructorActionLinks.shared'
export {
    ACTION_LINK_BULK_REASON_PREFIX,
    useActionLinksBulkImpact,
    useBulkActionLinks,
} from './useInstructorActionLinks.bulk'
export type { ActionLinkBulkAction } from './useInstructorActionLinks.bulk'
export {
    fetchPublicActionLink,
    submitPublicActionLink,
} from './useInstructorActionLinks.public'

// ── Côté OF ─────────────────────────────────────────────────────────────────

/**
 * E-mail non parti (502 `ACTION_LINK_EMAIL_NOT_SENT`) : la demande a été créée PUIS
 * annulée — et, pour des pièces, la précédente rendue vivante. La console doit se
 * relire, sinon elle ne le montre qu'au rafraîchissement.
 */
function relireLaConsoleSiLEmailNEstPasParti(qc: ReturnType<typeof useQueryClient>, err: unknown) {
    if (codeDuRefusTransverse(err) === 'ACTION_LINK_EMAIL_NOT_SENT') invalidateActionLinks(qc)
}

export function useEmitProofRequest(entityId: string) {
    const qc = useQueryClient()
    return useMutation<{ ial_id: string }, Error, { instructorId: string }>({
        mutationFn: async ({ instructorId }) => {
            const res = await apiFetch(
                `${ENDPOINT}/${instructorId}/action-links?entity_id=${entityId}`,
                { method: 'POST', headers: headers(entityId) },
            )
            if (!res.ok) await handleApiError(res)
            return res.json()
        },
        onSuccess: () => invalidateActionLinks(qc),
        onError: (err) => relireLaConsoleSiLEmailNEstPasParti(qc, err),
    })
}

export function useEmitSelfDeclaration(entityId: string) {
    const qc = useQueryClient()
    return useMutation<
        { ial_id: string },
        Error,
        { email: string; proposedNature?: string | null; force?: boolean }
    >({
        mutationFn: async ({ email, proposedNature, force }) => {
            const res = await apiFetch(
                `${ENDPOINT}/action-links/self-declaration?entity_id=${entityId}`,
                {
                    method: 'POST',
                    headers: headers(entityId),
                    body: JSON.stringify({
                        email,
                        proposed_nature: proposedNature ?? null,
                        force: !!force,
                    }),
                },
            )
            if (!res.ok) await handleApiError(res)
            return res.json()
        },
        // Sans invalidation, l'invitation qu'on vient d'envoyer n'apparaissait
        // nulle part — c'est ce qui donnait le sentiment que « rien ne se passe ».
        onSuccess: () => invalidateActionLinks(qc),
        onError: (err) => relireLaConsoleSiLEmailNEstPasParti(qc, err),
    })
}

export function useResendActionLink(entityId: string) {
    const qc = useQueryClient()
    return useMutation<
        { status: string; resend_count: number; expires_at: string },
        Error,
        { ialId: string }
    >({
        mutationFn: async ({ ialId }) => {
            const res = await apiFetch(
                `${ENDPOINT}/action-links/${ialId}/resend?entity_id=${entityId}`,
                { method: 'POST', headers: headers(entityId) },
            )
            if (!res.ok) await handleApiError(res)
            return res.json()
        },
        onSuccess: () => invalidateActionLinks(qc),
    })
}

export function useRevokeActionLink(entityId: string) {
    const qc = useQueryClient()
    return useMutation<Record<string, unknown>, Error, { ialId: string }>({
        mutationFn: async ({ ialId }) => {
            const res = await apiFetch(
                `${ENDPOINT}/action-links/${ialId}/revoke?entity_id=${entityId}`,
                { method: 'POST', headers: headers(entityId) },
            )
            if (!res.ok) await handleApiError(res)
            return res.json()
        },
        onSuccess: () => invalidateActionLinks(qc),
    })
}

/** Pré-check à la saisie d'une invitation — jamais une garantie (la contrainte
 *  SQL rattrape le double onglet), mais c'est lui qui évite le doublon en amont. */
export async function checkRecipient(entityId: string, email: string): Promise<RecipientCheck> {
    const res = await apiFetch(
        `${ENDPOINT}/action-links/check-recipient?entity_id=${entityId}&email=${encodeURIComponent(email)}`,
        { headers: { 'x-entity-id': entityId } },
    )
    if (!res.ok) await handleApiError(res)
    return res.json()
}

/**
 * Compteurs de la console — pilotent la VISIBILITÉ de l'entrée de nav
 * (`total > 0` : un organisme qui n'utilise jamais les invitations ne voit
 * jamais l'entrée) et sa pastille. Moule `useExportsUnseenCount`.
 */
export function useActionLinksSummary(entityId: string, enabled = true) {
    return useQuery<ActionLinksSummary>({
        queryKey: queryKeys.instructors.actionLinksSummary(entityId),
        queryFn: async () => {
            const res = await apiFetch(
                `${ENDPOINT}/action-links/summary?entity_id=${entityId}`,
                { headers: { 'x-entity-id': entityId } },
            )
            if (!res.ok) await handleApiError(res)
            return res.json()
        },
        enabled: enabled && !!entityId,
        refetchInterval: 60 * 1000,
        staleTime: 30 * 1000,
    })
}

export function useActionLinksQueue(entityId: string, status = 'SUBMITTED', enabled = true) {
    return useQuery<{ items: ActionLinkQueueItem[]; next_cursor: string | null }>({
        queryKey: [...queryKeys.instructors.actionLinks(), status, entityId],
        queryFn: async () => {
            const res = await apiFetch(
                `${ENDPOINT}/action-links?entity_id=${entityId}&status_filter=${status}`,
                { headers: { 'x-entity-id': entityId } },
            )
            if (!res.ok) await handleApiError(res)
            return res.json()
        },
        enabled,
    })
}

interface ValidatePayload {
    items?: {
        item_type: 'REQUIREMENT' | 'COMPLIANCE'
        item_ref: string
        date?: string | null
        expiry_date?: string | null
        file_index?: number | null
    }[]
    self_declaration?: Record<string, unknown>
    constraints?: Record<string, unknown> | null
    /** PASS-007 — présent ⇒ flux quiz. Ce que l'organisme n'envoie PAS n'est
     *  pas validé, même si le questionnaire est réussi : c'est tout l'objet de
     *  la validation humaine (§7.1). */
    quiz_items?: { requirement_id: string; granted_at?: string | null }[]
}

export function useValidateActionLink(entityId: string) {
    const qc = useQueryClient()
    return useMutation<Record<string, unknown>, Error, { ialId: string; payload: ValidatePayload }>({
        mutationFn: async ({ ialId, payload }) => {
            const res = await apiFetch(
                `${ENDPOINT}/action-links/${ialId}/validate?entity_id=${entityId}`,
                { method: 'POST', headers: headers(entityId), body: JSON.stringify(payload) },
            )
            if (!res.ok) await handleApiError(res)
            return res.json()
        },
        onSuccess: async () => {
            // Valider un dépôt CRÉE (ou complète) une fiche du vivier. Le bloc de
            // trois `invalidateQueries` recopié à la main qui vivait ici oubliait
            // la quatrième clé — `['datatable-facets']`, les compteurs du panneau
            // de filtres — et le `refetchType: 'all'` : on valide depuis la
            // console, le vivier est donc DÉMONTÉ, sa query inactive était marquée
            // périmée sans jamais être rechargée. `invalidateDataTableListings`
            // couvre les quatre.
            await invalidateDataTableListings(qc, ENDPOINT, queryKeys.instructors.all)
            // La console et la pastille doivent bouger aussi : sans ça, la
            // demande validée restait « à valider » jusqu'au refetch naturel.
            invalidateActionLinks(qc)
            // Valider un dépôt POSE l'exigence : les lignes du cockpit changent de
            // statut. La file de validation est montée SUR la page du cockpit — le
            // tableau reste donc derrière, et sans cet appel il gardait l'ancien
            // statut pendant que l'en-tête, lui, avait déjà bougé.
            invalidateRequirementsMatrix(qc)
        },
    })
}

export function useRejectActionLink(entityId: string) {
    const qc = useQueryClient()
    return useMutation<Record<string, unknown>, Error, { ialId: string; reason: string }>({
        mutationFn: async ({ ialId, reason }) => {
            const res = await apiFetch(
                `${ENDPOINT}/action-links/${ialId}/reject?entity_id=${entityId}`,
                { method: 'POST', headers: headers(entityId), body: JSON.stringify({ reason }) },
            )
            if (!res.ok) await handleApiError(res)
            return res.json()
        },
        onSuccess: () => invalidateActionLinks(qc),
    })
}
